import type { TrackerKey } from "../../../../../../types/clientConfig";
import { ATTENTION_SECTIONS, type AttentionSection } from "./attention";

/**
 * Coach-facing filters on the client configuration list (sc-46615). The
 * matching itself happens in msb-api (POST client-configurations/filter, see
 * lib/api_helpers/client-configuration/config-filters.js) so it covers the
 * whole list, not just the rows the table has loaded.
 */

export type OnOff = "on" | "off";
export type ModeOption = "coach" | "client" | "off";
export type MatchMode = "all" | "any";

export const ON_OFF_SECTIONS = ["training", "goals"] as const;
export const MODE_SECTIONS = [
  "nutrition",
  "mealPlans",
  "dailyAccountabilities",
] as const;
export type OnOffSection = (typeof ON_OFF_SECTIONS)[number];
export type ModeSection = (typeof MODE_SECTIONS)[number];

export const TRACKER_FILTER_KEYS: TrackerKey[] = [
  "readiness",
  "sessionRpe",
  "bodyWeight",
  "bodyFat",
  "bodyMeasurements",
  "progressPhotos",
  "mealPhotos",
  "sleep",
  "steps",
  "water",
  "hrv",
  "restingHeartRate",
];

export const NO_TEMPLATE = "none";

export interface ConfigListFilters {
  needsAttention: boolean;
  cycle: OnOff[];
  template: string[];
  sections: Partial<Record<OnOffSection, OnOff[]>> &
    Partial<Record<ModeSection, ModeOption[]>>;
  trackers: TrackerKey[];
  match: MatchMode;
}

/** Counts per option over the whole list (not narrowed by active filters). */
export interface ConfigListFilterCounts {
  total: number;
  needsAttention: { any: number } & Record<AttentionSection, number>;
  cycle: Record<OnOff, number>;
  template: { id: string; name: string | null; count: number }[];
  sections: Record<OnOffSection, Record<OnOff, number>> &
    Record<ModeSection, Record<ModeOption, number>>;
  trackers: Record<TrackerKey, number>;
}

export const EMPTY_FILTERS: ConfigListFilters = {
  needsAttention: false,
  cycle: [],
  template: [],
  sections: {},
  trackers: [],
  match: "all",
};

/**
 * How many filters are on — what Match all / Match any combines. Each
 * section counts as its own filter, and so does each selected tracker (a
 * client can track several, so "all" vs "any" matters within Tracking too;
 * see msb-api config-filters.js).
 */
export const countActiveFilters = (filters: ConfigListFilters): number =>
  Number(filters.needsAttention) +
  Number(filters.cycle.length > 0) +
  Number(filters.template.length > 0) +
  Object.values(filters.sections).filter((options) => options?.length).length +
  filters.trackers.length;

/** Request body `filters` for POST client-configurations/filter. */
export const toApiFilters = (filters: ConfigListFilters) => ({
  ...(filters.needsAttention && { needsAttention: true }),
  ...(filters.cycle.length && { cycle: filters.cycle }),
  ...(filters.template.length && { template: filters.template }),
  sections: Object.fromEntries(
    Object.entries(filters.sections).filter(([, options]) => options?.length)
  ),
  ...(filters.trackers.length && { trackers: filters.trackers }),
});

// ---------------------------------------------------------------------------
// Local matching. The filter response carries each client's summary (msb-api
// summarizeConfigForFilters); once the list holds one for every client in a
// view, that view is matched and counted here instead of asking again. Keep
// in sync with msb-api config-filters.js `parseFilters` / `matchesFilters` /
// `countFilterOptions`.
// ---------------------------------------------------------------------------

/** One client's filterable facts (msb-api summarizeConfigForFilters). */
export interface ConfigFilterSummary {
  needsAttention: AttentionSection[];
  cycle: OnOff;
  template: { id: string; name: string | null };
  sections: Record<OnOffSection, OnOff> & Record<ModeSection, ModeOption>;
  trackers: string[];
}

/** null = the client has no config, so it's left out of results and counts. */
export type ConfigFilterSummaries = Record<string, ConfigFilterSummary | null>;

const filterPredicates = (
  filters: ConfigListFilters
): ((summary: ConfigFilterSummary) => boolean)[] => {
  const predicates: ((summary: ConfigFilterSummary) => boolean)[] = [];
  if (filters.needsAttention) {
    predicates.push((summary) => summary.needsAttention.length > 0);
  }
  if (filters.cycle.length) {
    predicates.push((summary) => filters.cycle.includes(summary.cycle));
  }
  if (filters.template.length) {
    predicates.push((summary) =>
      filters.template.includes(summary.template.id)
    );
  }
  for (const [key, options] of Object.entries(filters.sections)) {
    if (!options?.length) continue;
    predicates.push((summary) =>
      (options as string[]).includes(
        summary.sections[key as OnOffSection | ModeSection]
      )
    );
  }
  // One predicate per tracker: a client can track several at once.
  for (const tracker of filters.trackers) {
    predicates.push((summary) => summary.trackers.includes(tracker));
  }
  return predicates;
};

const countSummaries = (
  summaries: ConfigFilterSummary[]
): ConfigListFilterCounts => {
  const zeroes = <K extends string>(keys: readonly K[]) =>
    Object.fromEntries(keys.map((key) => [key, 0])) as Record<K, number>;
  const counts: ConfigListFilterCounts = {
    total: summaries.length,
    needsAttention: { any: 0, ...zeroes(ATTENTION_SECTIONS) },
    cycle: zeroes(["on", "off"] as const),
    template: [],
    sections: {
      ...(Object.fromEntries(
        ON_OFF_SECTIONS.map((key) => [key, zeroes(["on", "off"] as const)])
      ) as Record<OnOffSection, Record<OnOff, number>>),
      ...(Object.fromEntries(
        MODE_SECTIONS.map((key) => [
          key,
          zeroes(["coach", "client", "off"] as const),
        ])
      ) as Record<ModeSection, Record<ModeOption, number>>),
    },
    trackers: zeroes(TRACKER_FILTER_KEYS),
  };

  const templatesById = new Map<
    string,
    { id: string; name: string | null; count: number }
  >();
  for (const summary of summaries) {
    if (summary.needsAttention.length) counts.needsAttention.any++;
    for (const key of summary.needsAttention) counts.needsAttention[key]++;
    counts.cycle[summary.cycle]++;
    for (const [key, option] of Object.entries(summary.sections)) {
      const sectionCounts = counts.sections[
        key as OnOffSection | ModeSection
      ] as Record<string, number>;
      sectionCounts[option]++;
    }
    const trackerCounts = counts.trackers as Record<string, number>;
    for (const key of summary.trackers) {
      trackerCounts[key] = (trackerCounts[key] ?? 0) + 1;
    }
    const { id, name } = summary.template;
    const template = templatesById.get(id) ?? { id, name, count: 0 };
    template.count++;
    templatesById.set(id, template);
  }

  // Alphabetical, with "no template" last.
  counts.template = [...templatesById.values()].sort((a, b) => {
    if (a.id === NO_TEMPLATE) return 1;
    if (b.id === NO_TEMPLATE) return -1;
    return (a.name ?? "").localeCompare(b.name ?? "");
  });
  return counts;
};

/**
 * Matches and counts `clientIds` locally — null unless every one of them has
 * a summary (or is known to have no config), in which case the API's answer
 * would be the same.
 */
export const matchFiltersLocally = (
  summaries: ConfigFilterSummaries,
  clientIds: string[],
  filters: ConfigListFilters
): { counts: ConfigListFilterCounts; matchingIds: Set<string> } | null => {
  const present: { id: string; summary: ConfigFilterSummary }[] = [];
  for (const id of clientIds) {
    if (!(id in summaries)) return null;
    const summary = summaries[id];
    if (summary) present.push({ id, summary });
  }
  const predicates = filterPredicates(filters);
  const matches = (summary: ConfigFilterSummary) =>
    !predicates.length ||
    (filters.match === "any"
      ? predicates.some((predicate) => predicate(summary))
      : predicates.every((predicate) => predicate(summary)));
  return {
    counts: countSummaries(present.map(({ summary }) => summary)),
    matchingIds: new Set(
      present.filter(({ summary }) => matches(summary)).map(({ id }) => id)
    ),
  };
};

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((v) => typeof v === "string");

const pickAllowed = <T extends string>(
  value: unknown,
  allowed: readonly T[]
): T[] =>
  isStringArray(value)
    ? (value.filter((v) => (allowed as readonly string[]).includes(v)) as T[])
    : [];

/**
 * Reads filters from an untrusted source (the URL param). Anything
 * unknown is dropped rather than failing, so an old or hand-edited link
 * still loads.
 */
export const parseFilters = (raw: unknown): ConfigListFilters => {
  let value = raw;
  if (typeof value === "string") {
    try {
      value = JSON.parse(value);
    } catch {
      return EMPTY_FILTERS;
    }
  }
  if (!value || typeof value !== "object") return EMPTY_FILTERS;
  const input = value as Record<string, unknown>;
  const rawSections =
    input.sections && typeof input.sections === "object"
      ? (input.sections as Record<string, unknown>)
      : {};

  const sections: ConfigListFilters["sections"] = {};
  for (const key of ON_OFF_SECTIONS) {
    const options = pickAllowed(rawSections[key], ["on", "off"] as const);
    if (options.length) sections[key] = options;
  }
  for (const key of MODE_SECTIONS) {
    const options = pickAllowed(rawSections[key], [
      "coach",
      "client",
      "off",
    ] as const);
    if (options.length) sections[key] = options;
  }

  return {
    needsAttention: input.needsAttention === true,
    cycle: pickAllowed(input.cycle, ["on", "off"] as const),
    template: isStringArray(input.template) ? input.template : [],
    sections,
    trackers: pickAllowed(input.trackers, TRACKER_FILTER_KEYS),
    match: input.match === "any" ? "any" : "all",
  };
};

/** Compact form for the URL; null when nothing is on. */
export const serializeFilters = (filters: ConfigListFilters): string | null =>
  countActiveFilters(filters)
    ? JSON.stringify({ ...toApiFilters(filters), match: filters.match })
    : null;

// ---------------------------------------------------------------------------
// Persistence: applied filters live only in the URL, so reload / back /
// forward / a shared link keep them natively. A plain link to the list (e.g.
// back from a client's profile via a link) starts unfiltered on purpose.
// ---------------------------------------------------------------------------

export const FILTERS_QUERY_PARAM = "configFilters";

/** Mirrors the applied filters into the URL without navigating. */
export const syncFiltersToUrl = (filters: ConfigListFilters) => {
  if (typeof globalThis === "undefined" || !globalThis.history) return;
  const serialized = serializeFilters(filters);
  const url = new URL(globalThis.location.href);
  if (serialized) url.searchParams.set(FILTERS_QUERY_PARAM, serialized);
  else url.searchParams.delete(FILTERS_QUERY_PARAM);
  globalThis.history.replaceState(globalThis.history.state, "", url);
};

// ---------------------------------------------------------------------------
// Last filter responses, kept in memory in the browser. Switching group /
// coach remounts the list (see Athletes.js `key`), which would otherwise
// wait on a fresh POST client-configurations/filter before it can show chip
// counts or, with filters on, any row. Revisiting a scope renders from here
// right away while the list re-requests in the background.
// ---------------------------------------------------------------------------

interface CachedFilterScope {
  /** Counts cover the whole scope, so they don't depend on the filters. */
  counts: ConfigListFilterCounts;
  matchingIdsByFilterKey: Map<string, Set<string>>;
}

const MAX_CACHED_FILTER_SCOPES = 20;
const filterResultsCache = new Map<string, CachedFilterScope>();

/** One scope = one athletes list view: group, viewed coach and its clients. */
export const filterScopeKey = (
  filterGroup: string | undefined,
  organizationCoach: string | undefined,
  filterableIdsKey: string
) => `${filterGroup ?? ""}|${organizationCoach ?? ""}|${filterableIdsKey}`;

export const readCachedFilterResult = (
  scopeKey: string,
  filterKey: string
): {
  counts: ConfigListFilterCounts;
  matchingIds: Set<string> | null;
} | null => {
  const scope = filterResultsCache.get(scopeKey);
  if (!scope) return null;
  return {
    counts: scope.counts,
    matchingIds: scope.matchingIdsByFilterKey.get(filterKey) ?? null,
  };
};

export const rememberFilterResult = (
  scopeKey: string,
  filterKey: string,
  counts: ConfigListFilterCounts,
  matchingIds: Set<string>
) => {
  // Module state outlives a request on the server, so only cache in the
  // browser — one coach per tab.
  if (typeof window === "undefined") return;
  const scope = filterResultsCache.get(scopeKey);
  const matchingIdsByFilterKey = scope?.matchingIdsByFilterKey ?? new Map();
  matchingIdsByFilterKey.set(filterKey, matchingIds);
  // Re-insert so the Map's order doubles as least-recently-used.
  filterResultsCache.delete(scopeKey);
  filterResultsCache.set(scopeKey, { counts, matchingIdsByFilterKey });
  if (filterResultsCache.size > MAX_CACHED_FILTER_SCOPES) {
    const oldest = filterResultsCache.keys().next().value;
    if (oldest !== undefined) filterResultsCache.delete(oldest);
  }
};
